import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Language,
} from '@mui/icons-material';
import { THEME_COLORS } from '../../constants';
import { formatCelular } from '../../utils';
import { canEdit, canDelete } from '../../utils/permissionUtils';

/**
 * Componente de tarjeta de contacto para vista móvil
 */
const ContactCard = ({
  contact,
  isExpanded,
  onExpand,
  onEdit,
  onDelete,
  currentUser,
  showActions = true,
}) => {
  return (
    <Card
      sx={{
        marginBottom: 2,
        border: `1px solid ${THEME_COLORS.BORDER}`,
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: THEME_COLORS.WHITE,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: THEME_COLORS.BORDER_HOVER,
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <CardContent sx={{ padding: 3 }}>
        {/* Header con avatar y info básica */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  marginBottom: 0.5,
                }}
              >
                {contact.nombre}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ fontSize: '0.9rem', color: THEME_COLORS.SECONDARY }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: THEME_COLORS.TEXT_SECONDARY,
                    fontSize: '0.875rem',
                  }}
                >
                  {formatCelular(contact.celular)}
                </Typography>
                {/* Indicador de suscripción */}
                {contact.hasActiveSubscription ? (
                  <CheckCircleIcon sx={{ fontSize: '0.8rem', color: THEME_COLORS.TEXT_SECONDARY }} />
                ) : (
                  <CancelIcon sx={{ fontSize: '0.8rem', color: THEME_COLORS.TEXT_MUTED }} />
                )}
              </Box>
            </Box>
          </Box>

          {showActions && (
            <Box>
              {canEdit(currentUser, 'contacts') && (
                <IconButton
                  size="small"
                  sx={{ color: THEME_COLORS.SECONDARY }}
                  onClick={() => onEdit(contact)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {canDelete(currentUser, 'contacts') && (
                <IconButton
                  size="small"
                  sx={{ color: THEME_COLORS.SECONDARY }}
                  onClick={() => onDelete(contact)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        </Box>

        {/* Información básica */}
        <Box sx={{ marginBottom: 2 }}>
          {/* Email eWeLink */}
          {contact.email_ewelink && (
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
              <EmailIcon sx={{ fontSize: '0.9rem', color: THEME_COLORS.SECONDARY, marginRight: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  color: THEME_COLORS.TEXT_SECONDARY,
                  fontSize: '0.875rem',
                }}
              >
                {contact.email_ewelink}
              </Typography>
            </Box>
          )}

          {/* Distribuidor */}
          {contact.distributor_empresa && (
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
              <BusinessIcon sx={{ fontSize: '0.9rem', color: THEME_COLORS.SECONDARY, marginRight: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  color: THEME_COLORS.TEXT_SECONDARY,
                  fontSize: '0.875rem',
                }}
              >
                {contact.distributor_empresa}
                {contact.distributor_nombre && ` - ${contact.distributor_nombre}`}
              </Typography>
            </Box>
          )}

          {/* Ubicación */}
          {contact.ubicacion_texto && (
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
              <LocationIcon sx={{ fontSize: '0.9rem', color: THEME_COLORS.SECONDARY, marginRight: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  color: THEME_COLORS.TEXT_SECONDARY,
                  fontSize: '0.875rem',
                }}
              >
                {contact.ubicacion_texto}
              </Typography>
            </Box>
          )}

          {/* URL de ubicación como chip */}
          {contact.ubicacion_url && (
            <Box sx={{ marginTop: 1 }}>
              <Chip
                icon={<Language />}
                label="Ver ubicación"
                size="small"
                onClick={() => window.open(contact.ubicacion_url, '_blank')}
                sx={{
                  fontSize: '0.75rem',
                  height: 24,
                  backgroundColor: THEME_COLORS.LIGHT_GRAY,
                  color: THEME_COLORS.TEXT_SECONDARY,
                  '&:hover': {
                    backgroundColor: THEME_COLORS.BORDER,
                  },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Botón expandir/contraer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            paddingTop: 1,
            borderTop: `1px solid ${THEME_COLORS.BORDER}`,
          }}
        >
          <IconButton
            size="small"
            onClick={() => onExpand(contact.contact_id)}
            sx={{
              color: THEME_COLORS.SECONDARY,
              fontSize: '0.875rem',
            }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            <Typography
              variant="body2"
              sx={{
                marginLeft: 0.5,
                fontSize: '0.75rem',
                color: THEME_COLORS.TEXT_MUTED,
              }}
            >
              {isExpanded ? 'Menos detalles' : 'Más detalles'}
            </Typography>
          </IconButton>
        </Box>

        {/* Sección expandible de detalles */}
        <Collapse in={isExpanded}>
          <Divider sx={{ marginY: 2 }} />
          <Box sx={{ paddingTop: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: THEME_COLORS.TEXT_PRIMARY,
                fontWeight: 600,
                marginBottom: 2,
                fontSize: '0.875rem',
              }}
            >
              Detalles del Contacto
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: '0.8rem' }}>
                  ID:
                </Typography>
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.8rem' }}>
                  {contact.contact_id}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: '0.8rem' }}>
                  Creado:
                </Typography>
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.8rem' }}>
                  {new Date(contact.created_at).toLocaleDateString()}
                </Typography>
              </Box>

              {contact.updated_at && contact.updated_at !== contact.created_at && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: '0.8rem' }}>
                    Actualizado:
                  </Typography>
                  <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.8rem' }}>
                    {new Date(contact.updated_at).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ContactCard; 